import { useEffect, useMemo, useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import { AsideMenu } from 'components'
import { Device } from 'components/Device'
import Footer from 'components/Footer'
import { Loading } from 'components/UI'
import { useFamily } from 'context/hooks/useFamily'
import useQuery from 'context/hooks/useQuery'

import { getFamily } from 'services/family'
import {
  General,
  Health,
  Housing,
  Income,
  Members,
  Pregnancy,
  Nutrition,
  Education,
} from './Pages'

import './styles.scss'
import { type ApiDataType } from 'types/services/Family'
import { getInconsistencies } from 'services/inconsistencies'
import { type InconsistenciesType } from 'types/services/Inconsistencies'
import Accessibility from 'components/Accessibility'
import AsideMenuMobile from 'components/AsideMenuMobile'
import Button from 'components/UI/Buttom'

import IconAlert from 'assets/icons/infoIconLarge.svg'
import HeaderSections from 'components/HeaderSections'

type sectionNameTypes =
  | 'geral'
  | 'membros'
  | 'renda'
  | 'educacao'
  | 'habitacao'
  | 'saude'
  | 'gravidez'
  | 'nutricao'

const sectionNames = {
  geral: 'Geral',
  membros: 'Membros',
  renda: 'Renda',
  educacao: 'Educação',
  habitacao: 'Habitação',
  saude: 'Saúde',
  gravidez: 'Gravidez',
  nutricao: 'Nutrição',
}

function NucleoFamiliar() {
  const { type } = useParams()
  const { handleWithFamiliesData, handleWithInconsistencies, resetStates } =
    useFamily()
  const { cpf, nis, basesIds, familyCode } = useQuery()
  const device = Device.useDevice()
  const [isFamilyLoading, setIsFamilyLoading] = useState(true)
  const [isFamilyError, setIsFamilyError] = useState(false)
  const [reload, setReload] = useState(false)
  const [isGetInconsistencies, setIsGetInconsistencies] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>(
    'Ocorreu um erro ao buscar as informaçoes da família!'
  )

  const [menuIsExpanded, setMenuIsExpanded] = useState(false)

  const navigate = useNavigate()

  const { inconsistencies, infosKinship } = useFamily()

  function toggleMenu() {
    setMenuIsExpanded(!menuIsExpanded)
  }

  const devicePrefix = device.prefix()

  const currentWidth = device.currentWidth()

  const customLoadingStyle =
    devicePrefix === 'xs' ? { margin: '10rem auto' } : { margin: '15rem auto' }

  const contentPadding =
    currentWidth > 650 ? { paddingLeft: menuIsExpanded ? '260px' : '85px' } : {}

  const {
    familyMemberName,
    fieldName: fieldNameQuery,
    infosRecommedation,
    currenteBase,
    basesQuery,
  } = useQuery()

  const infosRecommedationQuery =
    infosRecommedation.length > 0 ? JSON.parse(infosRecommedation) : {}

  const links = [
    {
      path: '',
      title: 'Início',
    },
    {
      path: `geral?cpf=${infosKinship?.cpf ?? ''}&nis=${
        infosKinship?.nis ?? ''
      }&bases=${JSON.stringify(basesQuery)}`,
      title: 'Família',
    },
    {
      path: `${type ?? ''}?cpf=${infosKinship?.cpf ?? ''}&nis=${
        infosKinship?.nis ?? ''
      }&bases=${JSON.stringify(basesQuery)}`,
      title: type !== null ? `${sectionNames[type as sectionNameTypes]}` : '',
    },
    fieldNameQuery.length > 0 && {
      path: `${type ?? ''}?cpf=${cpf}&nis=${nis}&bases=${JSON.stringify(
        basesQuery
      )}${
        familyCode.length > 0 ? `&familyCode=${familyCode}` : ''
      }&fieldName=${fieldNameQuery}&familyMemberName=${familyMemberName}&currenteBase=${currenteBase}&area=Saúde&infosRecommedation=${JSON.stringify(
        {
          question: infosRecommedationQuery.question,
          answer: infosRecommedationQuery.answer,
          justification: infosRecommedationQuery.justification,
          isAlert: infosRecommedationQuery.isAlert,
        }
      )}`,
      title: 'Recomendação',
    },
  ].filter((link) => link)

  function renderByRoutes(route?: string) {
    switch (route) {
      case 'geral':
        return <General />

      case 'membros':
        return <Members />

      case 'renda':
        return <Income />

      case 'educacao':
        return <Education />

      case 'saude':
        return <Health />

      case 'habitacao':
        return <Housing />

      case 'gravidez':
        return <Pregnancy />

      case 'nutricao':
        return <Nutrition />

      default:
        return <General />
    }
  }

  function errorFamily() {
    return (
      <div className="container-NucleoFamiliar__error">
        <img src={IconAlert} alt="Icon ponto de exclamação" width={'150rem'} />
        <p>{errorMessage}</p>
        <div>
          <Button
            onClick={() => {
              navigate('/', { replace: true })
            }}
            title="Voltar"
            isGhost
            width="22rem"
          />
          <Button
            onClick={() => {
              setReload(!reload)
            }}
            title="Tentar novamente"
            type="primary"
            width="22rem"
          />
        </div>
      </div>
    )
  }

  useEffect(() => {
    return () => {
      resetStates()
    }
  }, [])

  useEffect(() => {
    getInconsistencies({ cpf, nis, codfamiliar: familyCode })
      .then((data: InconsistenciesType | undefined | null) => {
        if (data !== null && data !== undefined) {
          handleWithInconsistencies(data)
        }
      })
      .catch((err) => {
        if (err.response.status === 404) {
          handleWithInconsistencies(undefined)
        } else {
          handleWithInconsistencies(null)
        }
      })
      .finally(() => {
        setIsGetInconsistencies(true)
      })
  }, [reload])

  useEffect(() => {
    if (isGetInconsistencies) {
      setIsFamilyError(false)
      setIsFamilyLoading(true)
      const params = { cpf, nis, familyCode, bases: basesIds }

      getFamily(params)
        .then((data: ApiDataType[]) => {
          handleWithFamiliesData(data, basesIds, params)
        })

        .catch(async (err) => {
          setErrorMessage(
            err?.response?.data?.message ??
              'Ocorreu um erro ao buscar as informaçoes da família!'
          )

          setIsFamilyError(true)
          return await Promise.reject(err)
        })
        .finally(() => {
          setIsFamilyLoading(false)
        })
      setIsGetInconsistencies(false)
    }
  }, [isGetInconsistencies])

  const containerFamilyNucleus = useMemo(() => {
    if (isFamilyLoading) {
      return (
        <div className="container-NucleoFamiliar__loading">
          <Loading
            title="Buscando informações da família..."
            styles={customLoadingStyle}
          />
        </div>
      )
    } else if (isFamilyError) {
      return errorFamily()
    }
    return (
      <div className="container-NucleoFamiliar__content" style={contentPadding}>
        <Accessibility />
        {currentWidth < 651 && <AsideMenuMobile />}
        <div className="main" style={{ padding: '3rem' }}>
          <HeaderSections
            links={links}
            title={infosKinship?.name ?? ''}
            inconsistencies={inconsistencies}
          />
          {renderByRoutes(type)}
        </div>
        {!isFamilyError && !isFamilyLoading && <Footer />}
      </div>
    )
  }, [isFamilyLoading, isFamilyError, type, currentWidth, contentPadding])

  return (
    <>
      {!isFamilyLoading && !isFamilyError && currentWidth >= 651 && (
        <AsideMenu menuIsExpanded={menuIsExpanded} toggleMenu={toggleMenu} />
      )}
      <section
        className={`container-NucleoFamiliar ${devicePrefix ?? 'undefined'}`}
      >
        {containerFamilyNucleus}
      </section>
    </>
  )
}

export default NucleoFamiliar
